import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload, Loader2, FileSpreadsheet, CheckCircle, AlertCircle, X, ArrowLeft,
  Search, RefreshCw, Banknote, TrendingUp, Sparkles, Trash2, Eye, ArrowRight, ChevronDown, ChevronRight
} from "lucide-react";
import LogLayout from "../components/LogLayout";
import { getAuthHeaders } from "../lib/auth";
import { Button } from "../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;
const formatFCFA = (n) => `${Number(n || 0).toLocaleString("fr-FR")} F`;

export default function LogBankStatementsPage() {
  const navigate = useNavigate();
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedStmt, setSelectedStmt] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/log/bank-statements`, { headers: getHeaders() });
      setStatements(res.data.statements || []);
    } catch {
      toast.error("Erreur chargement");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll if any statement is "processing"
  useEffect(() => {
    const anyProcessing = statements.some(s => s.processing_status === "processing");
    if (!anyProcessing) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [statements, load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 30 * 1024 * 1024) return toast.error("Fichier trop volumineux (max 30 MB)");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/log/bank-statements/upload`, fd, {
        headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
      });
      toast.success(res.data.message);
      e.target.value = "";
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'analyse");
    } finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce relevé ?")) return;
    try {
      await axios.delete(`${API}/log/bank-statements/${id}`, { headers: getHeaders() });
      toast.success("Relevé supprimé");
      await load();
    } catch {
      toast.error("Erreur suppression");
    }
  };

  return (
    <LogLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/log/invoices")} className="p-2 rounded-lg hover:bg-white/5 text-slate-400">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
                <Banknote className="w-6 h-6 text-emerald-400" /> Rapprochement bancaire
              </h1>
              <p className="text-xs text-slate-400 mt-0.5">Importez votre relevé pour rapprocher virements et chèques automatiquement</p>
            </div>
          </div>
          <input ref={fileRef} type="file" accept=".pdf,.xls,.xlsx,.xlsm" onChange={handleUpload} className="hidden" data-testid="bank-stmt-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-emerald-600 hover:bg-emerald-500 text-white gap-2" data-testid="bank-stmt-upload-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? "Envoi…" : "Importer un relevé bancaire"}
          </Button>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-400" /></div>
        ) : statements.length === 0 ? (
          <EmptyState onUpload={() => fileRef.current?.click()} />
        ) : (
          <div className="space-y-3" data-testid="bank-stmt-list">
            {statements.map(s => (
              <StatementCard key={s.id} stmt={s} onOpen={() => setSelectedStmt(s.id)} onDelete={() => handleDelete(s.id)} />
            ))}
          </div>
        )}

        {selectedStmt && (
          <StatementDetailModal id={selectedStmt} onClose={() => { setSelectedStmt(null); load(); }} />
        )}
      </div>
    </LogLayout>
  );
}

function EmptyState({ onUpload }) {
  return (
    <div className="text-center py-16 bg-slate-900/40 border border-white/5 rounded-2xl">
      <Banknote className="w-12 h-12 text-emerald-400/60 mx-auto mb-4" />
      <h3 className="text-base font-semibold text-white mb-2">Aucun relevé importé</h3>
      <p className="text-sm text-slate-400 mb-6 max-w-md mx-auto">Téléchargez votre relevé depuis l'espace bancaire de votre banque (PDF ou Excel), puis importez-le ici. CMES extrait automatiquement les virements et chèques reçus puis vous propose les rapprochements.</p>
      <Button onClick={onUpload} className="bg-emerald-600 hover:bg-emerald-500 text-white gap-2"><Upload className="w-4 h-4" /> Importer le premier relevé</Button>
    </div>
  );
}

function StatementCard({ stmt, onOpen, onDelete }) {
  const isProcessing = stmt.processing_status === "processing";
  const isFailed = stmt.processing_status === "failed";
  const isReady = stmt.processing_status === "ready" || (!stmt.processing_status && (stmt.credits_count || 0) > 0);
  const applied = stmt.applied_count || 0;
  const total = stmt.credits_count || 0;
  const progress = total > 0 ? Math.round((applied / total) * 100) : 0;

  return (
    <div className="bg-slate-900/60 border border-white/10 rounded-xl p-4 hover:border-emerald-500/30 transition-colors" data-testid={`stmt-card-${stmt.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-bold text-white truncate">{stmt.bank_name || stmt.filename || "Relevé bancaire"}</h3>
            {isProcessing && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> En cours…</span>}
            {isReady && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300">{total} crédits</span>}
            {isFailed && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-300">Échec</span>}
          </div>
          <p className="text-xs text-slate-500 mt-1">{stmt.filename}</p>
          {(stmt.period_start || stmt.period_end) && (
            <p className="text-[11px] text-slate-400 mt-1">
              Période : <span className="text-slate-300">{stmt.period_start || "?"}</span> → <span className="text-slate-300">{stmt.period_end || "?"}</span>
            </p>
          )}
          {stmt.processing_message && <p className="text-[11px] text-slate-400 mt-1 italic">{stmt.processing_message}</p>}
        </div>
        <div className="flex items-center gap-2">
          {isReady && (
            <Button onClick={onOpen} className="bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5" data-testid={`stmt-open-${stmt.id}`}>
              <Eye className="w-4 h-4" /> Détail
            </Button>
          )}
          <button onClick={onDelete} className="p-2 rounded-lg hover:bg-red-500/10 text-slate-500 hover:text-red-400">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      {isReady && (
        <div className="grid grid-cols-4 gap-2 mt-3 text-center text-xs">
          <div className="bg-slate-950/60 rounded-lg p-2"><p className="text-[10px] text-slate-500">Total</p><p className="font-bold text-white">{formatFCFA(stmt.total_credits)}</p></div>
          <div className="bg-emerald-500/5 rounded-lg p-2"><p className="text-[10px] text-emerald-400">Suggérés</p><p className="font-bold text-emerald-300">{stmt.suggested_count ?? "?"}</p></div>
          <div className="bg-amber-500/5 rounded-lg p-2"><p className="text-[10px] text-amber-400">À valider</p><p className="font-bold text-amber-300">{stmt.low_confidence_count ?? "?"}</p></div>
          <div className="bg-slate-950/60 rounded-lg p-2"><p className="text-[10px] text-slate-500">Sans match</p><p className="font-bold text-slate-300">{stmt.no_match_count ?? "?"}</p></div>
        </div>
      )}
      {isReady && progress > 0 && (
        <div className="mt-3">
          <div className="flex justify-between text-[10px] text-slate-500 mb-1"><span>Rapprochements appliqués</span><span>{applied} / {total}</span></div>
          <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}
    </div>
  );
}

function StatementDetailModal({ id, onClose }) {
  const [stmt, setStmt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({}); // { line_id: { target_id, target_kind } }
  const [applying, setApplying] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/log/bank-statements/${id}`, { headers: getHeaders() });
      setStmt(res.data.statement);
    } catch {
      toast.error("Erreur chargement");
      onClose();
    } finally { setLoading(false); }
  }, [id, onClose]);

  useEffect(() => { load(); }, [load]);

  const credits = stmt?.credits || [];
  const filtered = credits.filter(c => {
    if (filter === "all") return true;
    if (filter === "suggested") return c.status === "suggested" && !c.applied;
    if (filter === "low") return c.status === "low_confidence" && !c.applied;
    if (filter === "no_match") return c.status === "no_match" && !c.applied;
    if (filter === "applied") return c.applied;
    return true;
  });

  const toggleSelect = (line, match) => {
    setSelected(prev => {
      const next = { ...prev };
      if (next[line.line_id]?.target_id === match.target_id) {
        delete next[line.line_id];
      } else {
        next[line.line_id] = { target_id: match.target_id, target_kind: match.target_kind };
      }
      return next;
    });
  };

  const selectAllSuggested = () => {
    const next = {};
    for (const c of credits) {
      if (c.status === "suggested" && !c.applied && c.matches?.length) {
        next[c.line_id] = { target_id: c.matches[0].target_id, target_kind: c.matches[0].target_kind };
      }
    }
    setSelected(next);
  };

  const applyAll = async () => {
    const applications = Object.entries(selected).map(([line_id, v]) => ({ line_id, ...v }));
    if (applications.length === 0) return toast.error("Sélectionnez au moins un rapprochement");
    if (!window.confirm(`Appliquer ${applications.length} rapprochement(s) ?`)) return;
    setApplying(true);
    try {
      const res = await axios.post(`${API}/log/bank-statements/${id}/apply`, { applications }, { headers: getHeaders() });
      toast.success(res.data.message);
      setSelected({});
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setApplying(false); }
  };

  if (loading || !stmt) {
    return (
      <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-emerald-400" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-6xl h-[92vh] flex flex-col" data-testid="stmt-detail-modal">
        {/* Header */}
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between gap-3 flex-wrap shrink-0">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-white flex items-center gap-2 truncate">
              <Banknote className="w-5 h-5 text-emerald-400" /> {stmt.bank_name || "Relevé"}
            </h2>
            <p className="text-xs text-slate-400">{stmt.account_number} · {stmt.period_start} → {stmt.period_end} · {stmt.credits_count} crédits · Total {formatFCFA(stmt.total_credits)}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={selectAllSuggested} variant="outline" size="sm" className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Tout sélectionner (suggérés)
            </Button>
            <Button onClick={applyAll} disabled={applying || Object.keys(selected).length === 0} className="bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5" data-testid="apply-all-btn">
              {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
              Appliquer ({Object.keys(selected).length})
            </Button>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/5 text-slate-400"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {/* Filters */}
        <div className="px-5 py-2 border-b border-white/5 flex gap-2 overflow-x-auto shrink-0">
          {[
            { v: "all", label: `Tout (${credits.length})`, color: "slate" },
            { v: "suggested", label: `Suggérés (${stmt.suggested_count || 0})`, color: "emerald" },
            { v: "low", label: `À valider (${stmt.low_confidence_count || 0})`, color: "amber" },
            { v: "no_match", label: `Sans match (${stmt.no_match_count || 0})`, color: "red" },
            { v: "applied", label: `Appliqués (${stmt.applied_count || 0})`, color: "blue" },
          ].map(f => (
            <button key={f.v} onClick={() => setFilter(f.v)} className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${filter === f.v ? "bg-emerald-500 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700"}`} data-testid={`filter-${f.v}`}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Credits list */}
        <div className="flex-1 overflow-y-auto p-5 space-y-2">
          {filtered.length === 0 ? (
            <p className="text-center text-slate-500 py-12">Aucune ligne pour ce filtre</p>
          ) : filtered.map(line => (
            <CreditLineRow key={line.line_id} line={line} selectedTarget={selected[line.line_id]?.target_id} onToggle={(match) => toggleSelect(line, match)} />
          ))}
        </div>
      </div>
    </div>
  );
}

function CreditLineRow({ line, selectedTarget, onToggle }) {
  const [expanded, setExpanded] = useState(line.status === "low_confidence" || (line.matches?.length || 0) > 1);
  const top = line.matches?.[0];
  const statusColor = {
    suggested: "border-emerald-500/30 bg-emerald-500/5",
    low_confidence: "border-amber-500/30 bg-amber-500/5",
    no_match: "border-slate-700 bg-slate-950/50",
  }[line.status] || "border-white/10 bg-slate-950/60";

  return (
    <div className={`rounded-lg border ${statusColor} ${line.applied ? "opacity-60" : ""}`}>
      {/* Main line */}
      <div className="flex items-center gap-3 p-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono text-slate-400">{line.operation_date || "?"}</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded uppercase font-bold tracking-wider"
              style={{
                background: line.kind === "virement" ? "rgba(59,130,246,0.15)" : line.kind === "cheque" ? "rgba(168,85,247,0.15)" : "rgba(100,116,139,0.15)",
                color: line.kind === "virement" ? "#93c5fd" : line.kind === "cheque" ? "#d8b4fe" : "#cbd5e1",
              }}>
              {line.kind}
            </span>
            {line.cheque_number && <span className="text-[10px] text-slate-400">N°{line.cheque_number}</span>}
            <span className="text-sm font-bold text-emerald-300 ml-auto">{formatFCFA(line.amount)}</span>
          </div>
          <p className="text-xs text-slate-300 mt-1 truncate" title={line.label}>{line.label}</p>
          {line.sender_hint && <p className="text-[11px] text-slate-500 mt-0.5">Émetteur : <span className="text-slate-300">{line.sender_hint}</span></p>}
        </div>
        {line.matches?.length > 1 && (
          <button onClick={() => setExpanded(e => !e)} className="text-slate-400 hover:text-white">
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        )}
      </div>

      {/* Applied indicator */}
      {line.applied && (
        <div className="px-3 pb-3 text-[11px] text-emerald-400 flex items-center gap-1">
          <CheckCircle className="w-3 h-3" /> Rapproché le {new Date(line.applied_at).toLocaleDateString("fr-FR")}
        </div>
      )}

      {/* Matches */}
      {!line.applied && line.matches?.length > 0 && (
        <div className="px-3 pb-3 space-y-1.5">
          {(expanded ? line.matches : line.matches.slice(0, 1)).map((m, idx) => (
            <div key={m.target_id}
              data-testid={`match-${line.line_id}-${m.target_id}`}
              onClick={() => onToggle(m)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all ${selectedTarget === m.target_id ? "border-emerald-400 bg-emerald-500/10" : "border-white/5 bg-slate-950 hover:border-white/15"}`}>
              <input type="checkbox" checked={selectedTarget === m.target_id} readOnly className="w-4 h-4 accent-emerald-500 cursor-pointer" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-bold text-white">{m.invoice_number}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded uppercase font-bold tracking-wider"
                    style={{ background: m.target_kind === "facture" ? "rgba(59,130,246,0.15)" : "rgba(168,85,247,0.15)",
                             color: m.target_kind === "facture" ? "#93c5fd" : "#d8b4fe" }}>
                    {m.target_kind === "facture" ? "Facture" : "Dossier chèque"}
                  </span>
                  {m.match_type === "total" && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-bold">PAIEMENT TOTAL</span>}
                  {m.match_type === "partiel" && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 font-bold">PAIEMENT PARTIEL</span>}
                  <span className="text-[10px] text-slate-400 truncate max-w-[180px]">{m.insurer_name}</span>
                  {m.amount_match && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300">MONTANT</span>}
                  {m.insurer_match && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300">ASSUREUR</span>}
                  {m.invoice_number_match && <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300">N° FACTURE</span>}
                  {m.cheque_number_match && <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300">N° CHÈQUE</span>}
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  Restant <span className="text-amber-300">{formatFCFA(m.remaining_amount)}</span>
                  {m.match_type === "partiel" && line.amount && (
                    <> · Après ce paiement, il restera <span className="text-amber-300 font-bold">{formatFCFA(m.remaining_amount - line.amount)}</span></>
                  )}
                  {m.match_type === "total" && <> · Écart ±{m.amount_diff_pct}%</>}
                  <> · Score <span className="text-emerald-300 font-mono">{m.score}/100</span></>
                </div>
              </div>
            </div>
          ))}
          {!expanded && line.matches.length > 1 && (
            <button onClick={() => setExpanded(true)} className="text-[11px] text-slate-400 hover:text-emerald-300 px-3">+ {line.matches.length - 1} autre(s) suggestion(s)</button>
          )}
        </div>
      )}
    </div>
  );
}
